import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  Check,
  GripVertical,
  Mail,
  Monitor,
  Moon,
  Navigation,
  Shield,
  Sun,
  User,
} from 'lucide-react';
import { useTheme } from 'next-themes';

import adminApi from '@/services/api/admin';
import {
  applyAdminDisplaySize,
  cacheAdminPreferences,
  DISPLAY_SIZE_OPTIONS,
  getCachedAdminPreferences,
  getCachedAdminUser,
  normalizeAdminPreferences,
  SIDEBAR_MODE_OPTIONS,
} from '@/services/adminPreferences';
import {
  adminRoutes,
  categoryOrder,
  getAccessibleCategoryIds,
} from '@/components/admin/navigation/adminRoutes';
import { canAccessSection as checkSectionAccess } from '@/components/admin/navigation/sectionAccess';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const APPEARANCE_OPTIONS = [
  { value: 'system', label: 'System', description: 'Follow this device', Icon: Monitor },
  { value: 'light', label: 'Light', description: 'Always use light', Icon: Sun },
  { value: 'dark', label: 'Dark', description: 'Always use dark', Icon: Moon },
];

const accountFromCache = () => {
  const user = getCachedAdminUser();
  return {
    name: user.name || 'Admin',
    email: user.email || '',
    role: user.role || 'admin',
    is_protected: Boolean(user.is_protected),
    allowed_sections: user.allowed_sections || [],
  };
};

const samePreferences = (left, right) => (
  left.appearance === right.appearance
  && left.display_size === right.display_size
  && left.receive_release_notes === right.receive_release_notes
  && left.sidebar_mode === right.sidebar_mode
  && left.navigation_order.join('|') === right.navigation_order.join('|')
  && left.navigation_hidden.join('|') === right.navigation_hidden.join('|')
);

const previewPreferences = (preferences) => {
  window.dispatchEvent(new CustomEvent('admin-preferences-changed', { detail: preferences }));
};

const AdminPreferencesDialog = ({ open, onOpenChange, returnFocusTarget }) => {
  const { setTheme } = useTheme();
  const setThemeRef = useRef(setTheme);
  const [account, setAccount] = useState(accountFromCache);
  const [preferences, setPreferences] = useState(getCachedAdminPreferences);
  const [savedPreferences, setSavedPreferences] = useState(getCachedAdminPreferences);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('account');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [draggedCategory, setDraggedCategory] = useState(null);

  useEffect(() => {
    setThemeRef.current = setTheme;
  }, [setTheme]);

  useEffect(() => {
    if (!open) return undefined;

    let active = true;
    setLoading(true);
    setActiveTab('account');
    setMessage('');
    setError('');
    adminApi.adminUsers.getMyPreferences()
      .then((response) => {
        if (!active) return;
        const next = normalizeAdminPreferences(response.preferences);
        setAccount(response.account);
        setPreferences(next);
        setSavedPreferences(next);
        cacheAdminPreferences(next, response.account);
        setThemeRef.current(next.appearance);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || 'Unable to load your preferences.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [open]);

  const permissionSummary = useMemo(() => {
    if (account.is_protected) return 'Protected administrator · All sections';
    const sections = account.allowed_sections || [];
    return sections.length
      ? `${sections.length} permitted section${sections.length === 1 ? '' : 's'}`
      : 'No platform sections assigned';
  }, [account]);

  const accessibleCategories = useMemo(() => getAccessibleCategoryIds(
    (sectionId) => checkSectionAccess(sectionId, account),
  ), [account]);

  const orderedAccessibleCategories = preferences.navigation_order.filter(
    (categoryId) => accessibleCategories.includes(categoryId),
  );

  const updatePreference = (key, value) => {
    setMessage('');
    setError('');
    setPreferences((current) => {
      const next = { ...current, [key]: value };
      if (key.startsWith('navigation_') || key === 'sidebar_mode') previewPreferences(next);
      return next;
    });
    if (key === 'appearance') setTheme(value);
    if (key === 'display_size') applyAdminDisplaySize(value);
  };

  const updateAccessibleOrder = (nextAccessibleOrder) => {
    updatePreference('navigation_order', [
      ...nextAccessibleOrder,
      ...preferences.navigation_order.filter(
        (categoryId) => !accessibleCategories.includes(categoryId),
      ),
    ]);
  };

  const moveCategory = (categoryId, delta) => {
    const currentIndex = orderedAccessibleCategories.indexOf(categoryId);
    const nextIndex = currentIndex + delta;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= orderedAccessibleCategories.length) return;
    const next = [...orderedAccessibleCategories];
    [next[currentIndex], next[nextIndex]] = [next[nextIndex], next[currentIndex]];
    updateAccessibleOrder(next);
  };

  const dropCategory = (targetCategory) => {
    if (!draggedCategory || draggedCategory === targetCategory) return;
    const next = orderedAccessibleCategories.filter((categoryId) => categoryId !== draggedCategory);
    next.splice(next.indexOf(targetCategory), 0, draggedCategory);
    updateAccessibleOrder(next);
    setDraggedCategory(null);
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && !saving && !samePreferences(preferences, savedPreferences)) {
      setTheme(savedPreferences.appearance);
      applyAdminDisplaySize(savedPreferences.display_size);
      setPreferences(savedPreferences);
      previewPreferences(savedPreferences);
    }
    onOpenChange(nextOpen);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await adminApi.adminUsers.updateMyPreferences(preferences);
      const next = normalizeAdminPreferences(response.preferences);
      setAccount(response.account);
      setPreferences(next);
      setSavedPreferences(next);
      cacheAdminPreferences(next, response.account);
      setTheme(next.appearance);
      setMessage('Preferences saved.');
    } catch (requestError) {
      setError(requestError.message || 'Unable to save your preferences.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1rem)] max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto p-4 sm:p-6"
        onCloseAutoFocus={(event) => {
          if (!returnFocusTarget) return;
          event.preventDefault();
          returnFocusTarget.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Personal preferences</DialogTitle>
          <DialogDescription>
            Manage your own display, navigation, and email choices. Platform settings remain separate.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-sm text-muted-foreground" role="status">
            Loading preferences…
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="min-w-0">
            <TabsList className="grid h-auto w-full grid-cols-4">
              <TabsTrigger value="account" className="px-1.5 sm:px-3">Account</TabsTrigger>
              <TabsTrigger value="display" className="px-1.5 sm:px-3">Display</TabsTrigger>
              <TabsTrigger value="navigation" className="px-1.5 sm:px-3">Navigation</TabsTrigger>
              <TabsTrigger value="email" className="px-1.5 sm:px-3">Email</TabsTrigger>
            </TabsList>

            <TabsContent value="account" className="space-y-4 pt-3">
              <div className="flex min-w-0 items-start gap-3 rounded-lg border p-4">
                <User className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
                <div className="min-w-0">
                  <p className="font-medium break-words">{account.name || 'Admin'}</p>
                  <p className="text-sm text-muted-foreground break-all">{account.email}</p>
                </div>
              </div>
              <div className="flex min-w-0 items-start gap-3 rounded-lg border p-4">
                <Shield className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
                <div className="min-w-0">
                  <p className="font-medium capitalize">{account.role || 'Admin'}</p>
                  <p className="text-sm text-muted-foreground">{permissionSummary}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Roles and permissions are read-only here. A protected administrator manages them in Settings &amp; Platform.
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="display" className="space-y-5 pt-3">
              <fieldset>
                <legend className="mb-2 text-sm font-medium">Appearance</legend>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {APPEARANCE_OPTIONS.map(({ value, label, description, Icon }) => (
                    <label key={value} className={`cursor-pointer rounded-lg border p-3 transition-colors ${preferences.appearance === value ? 'border-amber-600 bg-amber-50 dark:bg-amber-950/30' : 'hover:bg-muted/60'}`}>
                      <input
                        type="radio"
                        name="appearance"
                        value={value}
                        checked={preferences.appearance === value}
                        onChange={() => updatePreference('appearance', value)}
                        className="sr-only"
                      />
                      <span className="flex items-center gap-2 font-medium"><Icon className="h-4 w-4" />{label}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">{description}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset>
                <legend className="mb-2 text-sm font-medium">Display size</legend>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {DISPLAY_SIZE_OPTIONS.map(({ value, label, scale }) => (
                    <label key={value} className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors ${preferences.display_size === value ? 'border-amber-600 bg-amber-50 dark:bg-amber-950/30' : 'hover:bg-muted/60'}`}>
                      <span>
                        <span className="font-medium">{label}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{scale}</span>
                      </span>
                      <input
                        type="radio"
                        name="display-size"
                        value={value}
                        checked={preferences.display_size === value}
                        onChange={() => updatePreference('display_size', value)}
                      />
                    </label>
                  ))}
                </div>
              </fieldset>
            </TabsContent>

            <TabsContent value="navigation" className="space-y-5 pt-3">
              <fieldset>
                <legend className="mb-2 text-sm font-medium">Desktop sidebar mode</legend>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {SIDEBAR_MODE_OPTIONS.map(({ value, label, description }) => (
                    <label key={value} className={`cursor-pointer rounded-lg border p-3 transition-colors ${preferences.sidebar_mode === value ? 'border-amber-600 bg-amber-50 dark:bg-amber-950/30' : 'hover:bg-muted/60'}`}>
                      <input
                        type="radio"
                        name="sidebar-mode"
                        value={value}
                        checked={preferences.sidebar_mode === value}
                        onChange={() => updatePreference('sidebar_mode', value)}
                        className="sr-only"
                      />
                      <span className="flex items-center gap-2 font-medium"><Navigation className="h-4 w-4" />{label}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">{description}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-medium">Cockpit destinations</h3>
                    <p className="text-xs text-muted-foreground">Drag or use the arrow buttons to reorder. Hidden items remain in mobile More.</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const defaults = normalizeAdminPreferences({
                        ...preferences,
                        sidebar_mode: 'rail',
                        navigation_order: categoryOrder,
                        navigation_hidden: [],
                      });
                      setPreferences(defaults);
                      previewPreferences(defaults);
                    }}
                  >
                    Reset defaults
                  </Button>
                </div>
                <ul className="space-y-2" aria-label="Navigation destination order">
                  {orderedAccessibleCategories.map((categoryId, index) => {
                    const category = adminRoutes[categoryId];
                    const Icon = category.icon;
                    const hidden = preferences.navigation_hidden.includes(categoryId);
                    return (
                      <li
                        key={categoryId}
                        draggable
                        onDragStart={() => setDraggedCategory(categoryId)}
                        onDragEnd={() => setDraggedCategory(null)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => dropCategory(categoryId)}
                        className="flex items-center gap-2 rounded-lg border p-2"
                      >
                        <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground" aria-hidden="true" />
                        <Icon className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden="true" />
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">{category.label}</span>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          disabled={index === 0}
                          onClick={() => moveCategory(categoryId, -1)}
                          aria-label={`Move ${category.label} up`}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          disabled={index === orderedAccessibleCategories.length - 1}
                          onClick={() => moveCategory(categoryId, 1)}
                          aria-label={`Move ${category.label} down`}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Switch
                          checked={!hidden}
                          onCheckedChange={(checked) => updatePreference(
                            'navigation_hidden',
                            checked
                              ? preferences.navigation_hidden.filter((id) => id !== categoryId)
                              : [...preferences.navigation_hidden, categoryId],
                          )}
                          aria-label={`Show ${category.label}`}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="email" className="pt-3">
              <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
                <div className="flex min-w-0 gap-3">
                  <Mail className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
                  <div>
                    <label htmlFor="release-notes-preference" className="font-medium">Weekly release notes</label>
                    <p className="text-sm text-muted-foreground">Receive a weekly summary of improvements to Owaza.</p>
                  </div>
                </div>
                <Switch
                  id="release-notes-preference"
                  checked={preferences.receive_release_notes}
                  onCheckedChange={(checked) => updatePreference('receive_release_notes', checked)}
                  aria-label="Receive weekly release notes"
                />
              </div>
            </TabsContent>
          </Tabs>
        )}

        <div aria-live="polite" className="min-h-5 text-sm">
          {error && <p className="text-red-600" role="alert">{error}</p>}
          {message && <p className="flex items-center gap-1 text-green-700 dark:text-green-400"><Check className="h-4 w-4" />{message}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>Close</Button>
          <Button
            onClick={save}
            disabled={loading || saving || samePreferences(preferences, savedPreferences)}
          >
            {saving ? 'Saving…' : 'Save preferences'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

AdminPreferencesDialog.defaultProps = {
  open: false,
  onOpenChange: () => {},
  returnFocusTarget: null,
};

export default AdminPreferencesDialog;
